
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
// import * as Sentry from '@sentry/browser'
import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import useTranslation from 'next-translate/useTranslation'
import { Box, Text, SimpleGrid, AspectRatio, Heading, Skeleton } from '@chakra-ui/react'
import { isNil, head } from 'lodash-es'

import { initializeApollo } from 'web-queries/src/utils/apolloClient'
import {
  ArticleListItemFragment,
  LiveEventListDocument,
  LiveEventListItemFragment,
  useHomeContentsQuery,
  useHomeLiveContentsQuery,
  useLiveEventListQuery,
  HomeLiveListItemFragment,
} from 'web-queries/src/types/generated'
import dynamic from 'next/dynamic'
import Meta from '../components/Meta'
import PageContent from '../components/PageContent'
import HeadingText from '../components/HeadingText'
import ErrorBoundary from '../components/ErrorBoundary'
import HoverBox from '../components/HoverBox'
import { formatsToUrl } from '../utils/image'
import { getCurrentMonthRange, getTodayRange } from '../utils/date'

import type { InferGetStaticPropsType } from 'next'
import FestivalItem from '../components/FestivalItem'
import LiveCalendarView from '../components/LiveCalendarView'
import ArticleList from '../components/ArticleList'

const DynamicHomeLiveList = dynamic(() => import('../components/live/HomeLiveList'), {
  suspense: true,
})
export default function HomePage({
  todayStartString,
  todayEndString,
  monthStartDateString,
  monthEndDateString,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { t } = useTranslation('homePage')
  const [visibleDate, setVisibleDate] = useState(new Date(monthStartDateString))

  const {
    data,
    loading: homeDataLoading,
    error,
  } = useHomeLiveContentsQuery({
    variables: {
      limit: 48,
      todayStart: todayStartString,
      todayEnd: todayEndString,
    },
  })

  const {
    data: liveEventList,
    loading: liveEventLoading,
    refetch,
  } = useLiveEventListQuery({
    variables: {
      monthStartDateString,
      monthEndDateString,
    },
  })

  const { data: homeContentData, loading: homeContentLoading } = useHomeContentsQuery({
    variables: {
      limit: 12,
    },
  })
  const upcomingLives = (data?.upcomingLives as HomeLiveListItemFragment[]) ?? null
  const todayLives = (data?.todayLives as HomeLiveListItemFragment[]) ?? null
  const recentlyArticles = (homeContentData?.recentlyArticles as ArticleListItemFragment[]) ?? null
  const recentlyReleasedAlbums = homeContentData?.recentlyReleasedAlbums ?? null
  const currentFestivals = data?.currentFestivals ?? null
  const monthlyLives = (liveEventList?.lives as LiveEventListItemFragment[]) ?? null

  if (error) {
    // Sentry.captureException(error)
    return (
      <Box w="100%" textAlign="center" position="fixed" left="50%" top="50%" transform="translate(-50%, -50%)">
        <Text fontSize="30px" dangerouslySetInnerHTML={{ __html: t('common:something-wrong') }} />
      </Box>
    )
  }

  return (
    <ErrorBoundary>
      <Meta title={t('title')} description={t('description')} uri="/" keywords={t('keywords')} />
      <PageContent>
        <Heading as="h1" className="screen-reader-only">
          {t('title')} - {t('description')}
        </Heading>
        <Box position="relative" marginTop="24px">
          <Box marginBottom="24px">
            <Skeleton isLoaded={!liveEventLoading && !isNil(monthlyLives)}>
              <LiveCalendarView
                visibleDate={visibleDate}
                lives={monthlyLives}
                onDateChange={changedDate => {
                  setVisibleDate(changedDate)
                  const monthDateStrings = getCurrentMonthRange(changedDate)
                  refetch(monthDateStrings)
                }}
              />
            </Skeleton>
          </Box>
          {currentFestivals && currentFestivals.length > 0 && (
            <Box>
              <HeadingText>{t('common:current-festival')}</HeadingText>
              <SimpleGrid columns={[1, 1, 2, 2, 3]} gap="16px">
                {currentFestivals?.flatMap(festival =>
                  isNil(festival)
                    ? []
                    : [
                        <HoverBox key={`festival-${festival.id}`} position="relative">
                          <FestivalItem live={festival} />
                        </HoverBox>,
                      ]
                )}
              </SimpleGrid>
            </Box>
          )}

          <Skeleton isLoaded={!homeDataLoading} minH="300px">
            <DynamicHomeLiveList lives={todayLives} title={t('today-live')} emptyText={t('today-live-not-found')} />
          </Skeleton>
          <Skeleton isLoaded={!homeDataLoading} minH="300px">
            <DynamicHomeLiveList
              lives={upcomingLives}
              title={t('upcoming-live')}
              emptyText={t('upcoming-live-not-found')}
            />
          </Skeleton>
          <Skeleton isLoaded={!homeContentLoading && !isNil(recentlyArticles)}>
            <ArticleList articles={recentlyArticles} title={t('article:music-related-articles')} />
            <Box>
              <HeadingText>{t('latest-released-album')}</HeadingText>
              <SimpleGrid flexDirection="column" columns={[1, 2, 3, 4]} gap="16px">
                {recentlyReleasedAlbums?.flatMap(album =>
                  isNil(album)
                    ? []
                    : [
                        <HoverBox key={`recently-added-album-${album.id}`}>
                          <Link href={`/albums/${album.id}`} passHref>
                            <Box as="a">
                              <AspectRatio ratio={1} position="relative">
                                <Image
                                  src={`${
                                    formatsToUrl(head(album.images)?.formats) ?? '/images/music_record_disc.png'
                                  }`}
                                  objectFit="contain"
                                  layout="fill"
                                  alt={`앨범 음원 ${album.name}의 커버`}
                                />
                              </AspectRatio>
                              <Box padding="8px" bgColor="whiteAlpha.300">
                                <Text>
                                  {album.musician?.name} - {album.name}
                                </Text>
                              </Box>
                            </Box>
                          </Link>
                        </HoverBox>,
                      ]
                )}
              </SimpleGrid>
            </Box>
          </Skeleton>
        </Box>
      </PageContent>
    </ErrorBoundary>
  )
}

 const _getStaticProps = async () => {
  const apolloClient = initializeApollo({})

  const { todayStartString, todayEndString } = getTodayRange()

  const monthDateStrings = getCurrentMonthRange(new Date())

  await apolloClient.query({
    query: LiveEventListDocument,
    variables: monthDateStrings,
  })

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      todayStartString,
      todayEndString,
      ...monthDateStrings,
    },
    revalidate: 60 * 5,
  }
}


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/home',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  